/* ═══════════════════════════════════════════════════════════════════════════════
   Dashboard Filter Types: Centralized BI-style filter system
   ═══════════════════════════════════════════════════════════════════════════════ */

// ─── Date Presets ────────────────────────────────────────────────────────────
export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'ytd'
  | 'last_12'
  | 'all_time'
  | 'custom';

// ─── Variation Status ────────────────────────────────────────────────────────
export type VariationStatusFilter = 'Approved' | 'Pending' | 'Rejected' | 'Void';

// ─── Date Range ──────────────────────────────────────────────────────────────
export interface DateRange {
  startMonth: number; // 1-12
  startYear: number;  // e.g. 2025
  endMonth: number;   // 1-12
  endYear: number;    // e.g. 2026
}

// ─── Core Filter State ───────────────────────────────────────────────────────
export interface DashboardFilterState {
  /** Selected project IDs. Empty array = all projects (no filter). */
  selectedProjectIds: string[];
  /** Selected builder/client names. Empty array = all builders. */
  selectedBuilderNames: string[];
  /** Date range (month-level granularity). */
  dateRange: DateRange;
  /** Which preset is active (or 'custom' for manual range). */
  datePreset: DatePreset;
  /** Project status filter. */
  projectStatus: 'active' | 'completed' | 'all';
  /** Variation status filter for variation-specific charts. Empty = all. */
  variationStatuses: VariationStatusFilter[];
}

// ─── Reducer Actions ─────────────────────────────────────────────────────────
export type DashboardFilterAction =
  | { type: 'SET_PROJECTS'; payload: string[] }
  | { type: 'SET_BUILDERS'; payload: string[] }
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_PRESET'; payload: DatePreset }
  | { type: 'SET_STATUS'; payload: 'active' | 'completed' | 'all' }
  | { type: 'SET_VARIATION_STATUSES'; payload: VariationStatusFilter[] }
  | { type: 'CLEAR_ALL' };

// ─── Context Value ───────────────────────────────────────────────────────────
export interface DashboardFilterContextValue {
  filters: DashboardFilterState;
  dispatch: React.Dispatch<DashboardFilterAction>;
  /** Computed: number of active filters (non-default selections). */
  activeFilterCount: number;
  /** Whether any filter is active. */
  isFiltered: boolean;
  /** Computed set of project IDs after applying builder + status filters. */
  effectiveProjectIds: string[] | null; // null = no filter (all projects)
  /** Human-readable summary of active filters. */
  filterSummary: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Given a DatePreset, returns the corresponding DateRange.
 */
export function getDateRangeFromPreset(preset: DatePreset): DateRange {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1-12
  const currentYear = now.getFullYear();

  switch (preset) {
    case 'this_month':
      return {
        startMonth: currentMonth,
        startYear: currentYear,
        endMonth: currentMonth,
        endYear: currentYear,
      };

    case 'last_month': {
      const lm = currentMonth === 1 ? 12 : currentMonth - 1;
      const ly = currentMonth === 1 ? currentYear - 1 : currentYear;
      return { startMonth: lm, startYear: ly, endMonth: lm, endYear: ly };
    }

    case 'this_quarter': {
      const qStart = Math.floor((currentMonth - 1) / 3) * 3 + 1;
      return {
        startMonth: qStart,
        startYear: currentYear,
        endMonth: currentMonth,
        endYear: currentYear,
      };
    }

    case 'last_quarter': {
      const currentQ = Math.floor((currentMonth - 1) / 3);
      const prevQ = currentQ === 0 ? 3 : currentQ - 1;
      const prevQYear = currentQ === 0 ? currentYear - 1 : currentYear;
      const qsm = prevQ * 3 + 1;
      const qem = prevQ * 3 + 3;
      return {
        startMonth: qsm,
        startYear: prevQYear,
        endMonth: qem,
        endYear: prevQYear,
      };
    }

    case 'ytd':
      return {
        startMonth: 1,
        startYear: currentYear,
        endMonth: currentMonth,
        endYear: currentYear,
      };

    case 'last_12': {
      const sm = currentMonth === 12 ? 1 : currentMonth + 1;
      const sy = currentMonth === 12 ? currentYear : currentYear - 1;
      return {
        startMonth: sm,
        startYear: sy,
        endMonth: currentMonth,
        endYear: currentYear,
      };
    }

    case 'all_time':
      return {
        startMonth: 1,
        startYear: 2000,
        endMonth: 12,
        endYear: currentYear + 1,
      };

    case 'custom':
      // Custom: return full range as default (caller should override)
      return {
        startMonth: 1,
        startYear: 2000,
        endMonth: 12,
        endYear: currentYear + 1,
      };
  }
}

/**
 * Converts a month/year pair to a sortable key "YYYY-MM".
 */
export function toMonthKey(month: number, year: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Checks if a given month/year falls within the DateRange (inclusive).
 */
export function isInDateRange(
  month: number,
  year: number,
  range: DateRange,
): boolean {
  const key = toMonthKey(month, year);
  const startKey = toMonthKey(range.startMonth, range.startYear);
  const endKey = toMonthKey(range.endMonth, range.endYear);
  return key >= startKey && key <= endKey;
}

/**
 * Checks if a "YYYY-MM" or "YYYY-MM-DD" date string falls within the DateRange.
 */
export function isDateStringInRange(
  dateStr: string,
  range: DateRange,
): boolean {
  const parts = dateStr.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  return isInDateRange(month, year, range);
}

// ─── URL Serialization ───────────────────────────────────────────────────────

const FILTER_PARAM_PREFIX = 'df_';

export function serializeFiltersToURL(
  filters: DashboardFilterState,
): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.selectedProjectIds.length > 0) {
    params.set(`${FILTER_PARAM_PREFIX}projects`, filters.selectedProjectIds.join(','));
  }

  if (filters.selectedBuilderNames.length > 0) {
    params.set(`${FILTER_PARAM_PREFIX}builders`, filters.selectedBuilderNames.join(','));
  }

  if (filters.datePreset !== 'all_time') {
    params.set(`${FILTER_PARAM_PREFIX}preset`, filters.datePreset);
  }

  if (filters.datePreset === 'custom') {
    params.set(
      `${FILTER_PARAM_PREFIX}range`,
      `${filters.dateRange.startYear}-${filters.dateRange.startMonth}_${filters.dateRange.endYear}-${filters.dateRange.endMonth}`,
    );
  }

  if (filters.projectStatus !== 'active') {
    params.set(`${FILTER_PARAM_PREFIX}status`, filters.projectStatus);
  }

  if (filters.variationStatuses.length > 0) {
    params.set(`${FILTER_PARAM_PREFIX}vstatus`, filters.variationStatuses.join(','));
  }

  return params;
}

export function deserializeFiltersFromURL(
  searchParams: URLSearchParams,
): Partial<DashboardFilterState> {
  const result: Partial<DashboardFilterState> = {};

  const projects = searchParams.get(`${FILTER_PARAM_PREFIX}projects`);
  if (projects) {
    result.selectedProjectIds = projects.split(',').filter(Boolean);
  }

  const builders = searchParams.get(`${FILTER_PARAM_PREFIX}builders`);
  if (builders) {
    result.selectedBuilderNames = builders.split(',').filter(Boolean);
  }

  const preset = searchParams.get(`${FILTER_PARAM_PREFIX}preset`);
  if (preset) {
    result.datePreset = preset as DatePreset;
    result.dateRange = getDateRangeFromPreset(preset as DatePreset);
  }

  const range = searchParams.get(`${FILTER_PARAM_PREFIX}range`);
  if (range && preset === 'custom') {
    const [start, end] = range.split('_');
    const [sy, sm] = start.split('-').map(Number);
    const [ey, em] = end.split('-').map(Number);
    if (sy && sm && ey && em) {
      result.dateRange = {
        startMonth: sm,
        startYear: sy,
        endMonth: em,
        endYear: ey,
      };
    }
  }

  const status = searchParams.get(`${FILTER_PARAM_PREFIX}status`);
  if (status === 'active' || status === 'completed' || status === 'all') {
    result.projectStatus = status;
  }

  const vstatus = searchParams.get(`${FILTER_PARAM_PREFIX}vstatus`);
  if (vstatus) {
    result.variationStatuses = vstatus
      .split(',')
      .filter((s): s is VariationStatusFilter =>
        ['Approved', 'Pending', 'Rejected', 'Void'].includes(s),
      );
  }

  return result;
}

// ─── Default State ───────────────────────────────────────────────────────────
export const DEFAULT_FILTER_STATE: DashboardFilterState = {
  selectedProjectIds: [],
  selectedBuilderNames: [],
  dateRange: getDateRangeFromPreset('all_time'),
  datePreset: 'all_time',
  projectStatus: 'active',
  variationStatuses: [],
};
